import { css, keyframes } from "@emotion/react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const serverUrl = process.env.BUN_PUBLIC_SERVER_URL?.replace("/rpc", "") || "http://localhost:3000";

type DrawState = {
  numbers: number[];
  lastNumber: number | null;
  excludedNumbers: number[];
};

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(192, 57, 43, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(192, 57, 43, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(192, 57, 43, 0);
  }
`;

const bounceIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const pageContainer = css`
  height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, var(--color-navy) 0%, var(--color-navy-mid) 100%);
  display: flex;
  align-items: stretch;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    overflow: auto;
  }
`;

const gridSection = css`
  flex: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
  min-width: 0;
  min-height: 0;
  max-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: none;
  }
`;

const numbersGrid = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
  flex: 1;
  min-height: 0;
  container-type: size;
  container-name: grid;

  @media (max-width: 768px) {
    padding: 12px;
    gap: 2px;
    border-radius: 12px;
  }
`;

const numbersRow = css`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  flex: 1;
  min-height: 0;

  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const numberCell = css`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: min(8cqh, 8cqw);
  color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;

  @supports not (font-size: 1cqh) {
    font-size: clamp(14px, 2.5vw, 32px);
  }
`;

const numberCellDrawn = css`
  background: var(--color-white);
  color: var(--color-navy);
  box-shadow: 0 2px 12px rgba(255, 255, 255, 0.3);
  animation: ${bounceIn} 0.3s ease-out;
`;

const numberCellEmpty = css`
  visibility: hidden;
`;

const numberCellExcluded = css`
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.15);
  text-decoration: line-through;
`;

const numberCellLast = css`
  background: var(--color-gold);
  color: var(--color-navy);
  box-shadow: 0 0 24px rgba(241, 196, 15, 0.8);
  font-weight: 800;
`;

const countDisplay = css`
  font-family: var(--font-heading);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  margin-top: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const lastNumberPanel = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  background: rgba(0, 0, 0, 0.15);
  border-left: 1px solid rgba(255, 255, 255, 0.06);

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding: 32px 16px;
  }
`;

const lastNumberLabel = css`
  font-family: var(--font-heading);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 3px;
`;

const lastNumberDisplay = css`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: var(--color-gold);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 10px 50px rgba(241, 196, 15, 0.5),
    inset 0 -5px 20px rgba(0, 0, 0, 0.15);
  animation: ${pulseAnimation} 2s ease-in-out infinite;

  @media (max-width: 768px) {
    width: 140px;
    height: 140px;
  }
`;

const lastNumberValue = css`
  font-family: var(--font-display);
  font-size: 100px;
  color: var(--color-navy);
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 70px;
  }
`;

const noNumberDisplay = css`
  font-family: var(--font-heading);
  font-size: 24px;
  color: rgba(255, 255, 255, 0.3);
`;

const totalDrawn = css`
  font-family: var(--font-heading);
  font-size: 48px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
`;

const totalDrawnLabel = css`
  font-family: var(--font-heading);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const connectionStatus = css`
  position: fixed;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  font-family: var(--font-heading);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const statusDot = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
`;

const statusDotConnected = css`
  background: var(--color-success);
  box-shadow: 0 0 6px var(--color-success);
`;

const statusDotDisconnected = css`
  background: var(--color-error);
`;

interface DrawDisplayPageProps {
  eventId: string;
}

export function DrawDisplayPage({ eventId }: DrawDisplayPageProps) {
  const { t } = useTranslation();
  const [drawState, setDrawState] = useState<DrawState>({
    numbers: [],
    lastNumber: null,
    excludedNumbers: [],
  });
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const streamUrl = `${serverUrl}/live/${eventId}/stream`;
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as DrawState;
        setDrawState(data);
      } catch (err) {
        console.error("Failed to parse draw update:", err);
      }
    };

    eventSource.onerror = () => {
      setConnected(false);
      // EventSource will automatically reconnect
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [eventId]);

  const drawnSet = new Set(drawState.numbers);
  const excludedSet = new Set(drawState.excludedNumbers);
  const rows = Array.from({ length: 9 }, (_, row) =>
    Array.from({ length: 10 }, (_, col) => row * 10 + col + 1),
  );

  return (
    <div css={pageContainer}>
      <div css={gridSection}>
        <div css={numbersGrid}>
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} css={numbersRow}>
              {row.map((num) => {
                if (num > 90) {
                  return <div key={num} css={[numberCell, numberCellEmpty]} />;
                }
                const isDrawn = drawnSet.has(num);
                const isExcluded = excludedSet.has(num);
                const isLast = num === drawState.lastNumber && !isExcluded;

                // Excluded numbers are greyed out
                if (isExcluded) {
                  return (
                    <div key={num} css={[numberCell, numberCellExcluded]}>
                      {num}
                    </div>
                  );
                }

                return (
                  <div
                    key={num}
                    css={[
                      numberCell,
                      isDrawn && numberCellDrawn,
                      isLast && numberCellLast,
                    ]}
                  >
                    {num}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <p css={countDisplay}>
          {t("liveDraw.count", { count: drawState.numbers.length })}
        </p>
      </div>

      <div css={lastNumberPanel}>
        <span css={lastNumberLabel}>{t("liveDraw.lastNumber")}</span>
        {drawState.lastNumber !== null ? (
          <div css={lastNumberDisplay} key={drawState.lastNumber}>
            <span css={lastNumberValue}>{drawState.lastNumber}</span>
          </div>
        ) : (
          <div css={lastNumberDisplay}>
            <span css={noNumberDisplay}>—</span>
          </div>
        )}
        <div>
          <p css={totalDrawn}>{drawState.numbers.length}/90</p>
          <p css={totalDrawnLabel}>{t("liveDraw.drawnNumbers")}</p>
        </div>
      </div>

      <div css={connectionStatus}>
        <span
          css={[statusDot, connected ? statusDotConnected : statusDotDisconnected]}
        />
        <span>{connected ? t("liveDraw.connected") : t("liveDraw.disconnected")}</span>
      </div>
    </div>
  );
}
